import {
  createDocumentWithContent,
  type ExcelContent,
  type ExcelSheet,
  type ExcelFormat,
} from '@/lib/documents';

/** Une feuille d'un classeur exporté. */
export interface WorkbookSheet {
  /** Nom de l'onglet (nettoyé automatiquement). */
  name?: string;
  /** Ligne d'en-tête (1re ligne, mise en forme en gras). */
  headers: string[];
  /** Une ligne par enregistrement. */
  rows: (string | number)[][];
}

const HEADER_BG = '#F1F5F9';

/** Nettoie un nom d'onglet Excel (31 caractères max, sans caractères interdits). */
function sanitizeSheetName(name: string, fallback: string): string {
  const clean = (name || fallback).replace(/[\\/:*?\[\]]/g, ' ').trim().slice(0, 31);
  return clean || fallback;
}

/** Construit une feuille Excel à partir d'un en-tête et de lignes. */
function makeSheet(sheet: WorkbookSheet, index: number): ExcelSheet {
  const headers = sheet.headers.length > 0 ? sheet.headers : [''];
  const cells: Record<string, string> = {};
  const formats: Record<string, ExcelFormat> = {};

  headers.forEach((h, c) => {
    cells[`0,${c}`] = h;
    formats[`0,${c}`] = { bold: true, bg: HEADER_BG };
  });

  sheet.rows.forEach((row, r) => {
    row.forEach((value, c) => {
      const text = value == null ? '' : String(value);
      if (text !== '') cells[`${r + 1},${c}`] = text;
    });
  });

  return {
    id: crypto.randomUUID(),
    name: sanitizeSheetName(sheet.name || '', `Feuille ${index + 1}`),
    cells,
    rows: sheet.rows.length + 1,
    cols: Math.max(headers.length, 1),
    formats,
    colWidths: {},
    rowHeights: {},
    merges: [],
    conditionalFormats: [],
  };
}

/**
 * Crée un classeur (fichier Excel) modifiable dans l'application Documents à
 * partir d'une ou plusieurs feuilles (en-têtes + lignes).
 */
export async function buildExcelWorkbook(
  userId: number,
  docName: string,
  sheets: WorkbookSheet[],
): Promise<void> {
  const built = (sheets.length > 0 ? sheets : [{ headers: [''], rows: [] }]).map(makeSheet);
  const content: ExcelContent = { sheets: built, activeSheetId: built[0].id };
  const safeName = docName.replace(/[\\/:*?"<>|]/g, ' ').slice(0, 80);
  await createDocumentWithContent(userId, safeName, 'excel', content, null);
}

/**
 * Crée une feuille de calcul (fichier Excel) modifiable dans l'application
 * Documents à partir d'un tableau d'en-têtes et de lignes.
 *
 * Réutilisé par les exports « Ouvrir dans Excel » (commandes, clients,
 * réponses de formulaire…). La première ligne est mise en forme (gras + fond).
 */
export async function buildExcelDocument(
  userId: number,
  docName: string,
  headers: string[],
  rows: (string | number)[][],
  options?: { sheetName?: string; headerBg?: string },
): Promise<void> {
  await buildExcelWorkbook(userId, docName, [
    { name: options?.sheetName, headers, rows },
  ]);
}